import type { Event as SentryEvent } from '@sentry/angular';

/** The marker Sentry's own scrubbing replaces a value with. */
const REDACTED_VALUE = '[Filtered]';

/** Stands in for a reference back into the value being copied, which would otherwise recurse forever. */
const CIRCULAR_REFERENCE_VALUE = '[Circular]';

/**
 * A key, query parameter or header whose value is a credential or the user's email address. Matched
 * anywhere in the name, so `otpCode`, `refreshToken` and `passwordChangeToken` are covered along with
 * `password`, `token` and `otp` themselves — and `appDeviceId`, which lets a remembered device skip
 * the second factor. `cookie` covers the `Cookie` header and Sentry's parsed `cookies`, whatever the
 * cookies are called.
 */
const SECRET_NAME_PATTERN = /password|token|otp|secret|deviceid|authorization|cookie|email/i;

/** An HTTP auth scheme with its credential, bare or quoted, as a message or breadcrumb quotes an `Authorization` header. */
const AUTH_SCHEME_PATTERN = /\b(Bearer|Basic)\s+(["']?)[A-Za-z0-9._~+/=-]+\2/gi;

/** A `name=value` pair of a query string: in a URL, on its own, or in a message quoting either. */
const QUERY_PARAMETER_PATTERN = /(^|[?&])([^=&#?\s"']+)=([^&#\s"']*)/g;

/** A `"name": value` pair of JSON text with a string or number value, even one cut off at the end of the text. */
const JSON_PROPERTY_PATTERN = /("([^"\\]*)"\s*:\s*)("(?:[^"\\]|\\.)*(?:"|$)|-?\d+(?:\.\d+)?)/g;

/**
 * Copies a value with every credential in it replaced by `[Filtered]`, at any depth: the value of
 * a key naming a secret, and a secret query parameter or JSON property inside any string — so a URL
 * carrying a token and a raw request body carrying a password are covered as well.
 *
 * An empty secret is kept, so a report still tells a missing value from a sent one. Only plain
 * objects and arrays are walked; anything else is passed on as it is. The input is never modified,
 * since a caller may still be using it.
 *
 * The backend carries the same file in `libs/shared/src/lib/sentry/`; keep the two in step.
 */
export function redactSecrets(value: unknown): unknown {
  return redactValue(value, new WeakSet<object>());
}

/**
 * The `beforeSend` of both apps' `Sentry.init`, the last step before an event leaves the
 * browser. It covers what no call site controls: the page URL Sentry attaches to every event,
 * the referrer, and the navigation breadcrumbs — each of which can carry the reset token once
 * the password reset page has been opened.
 */
export function redactSentryEvent<TEvent extends SentryEvent>(event: TEvent): TEvent {
  return redactSecrets(event) as TEvent;
}

function redactValue(value: unknown, ancestors: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return redactSecretsInText(value);
  }
  if (!Array.isArray(value) && !isPlainObject(value)) {
    return value;
  }
  if (ancestors.has(value)) {
    return CIRCULAR_REFERENCE_VALUE;
  }

  ancestors.add(value);
  const redactedValue = Array.isArray(value)
    ? value.map((entry: unknown) => redactValue(entry, ancestors))
    : Object.fromEntries(Object.entries(value).map(([key, entryValue]) => [key, redactEntry(key, entryValue, ancestors)]));
  ancestors.delete(value);
  return redactedValue;
}

function redactEntry(key: string, value: unknown, ancestors: WeakSet<object>): unknown {
  if (SECRET_NAME_PATTERN.test(key) && !isEmptyValue(value)) {
    return REDACTED_VALUE;
  }
  return redactValue(value, ancestors);
}

function redactSecretsInText(text: string): string {
  return text
    .replace(AUTH_SCHEME_PATTERN, (_credential: string, scheme: string, quote: string) => `${scheme} ${quote}${REDACTED_VALUE}${quote}`)
    .replace(JSON_PROPERTY_PATTERN, (property: string, prefix: string, name: string, value: string) =>
      SECRET_NAME_PATTERN.test(name) && value !== '""' ? `${prefix}"${REDACTED_VALUE}"` : property,
    )
    .replace(QUERY_PARAMETER_PATTERN, (parameter: string, separator: string, name: string, value: string) =>
      SECRET_NAME_PATTERN.test(decodeParameterName(name)) && value !== '' ? `${separator}${name}=${REDACTED_VALUE}` : parameter,
    );
}

/**
 * A query parameter's name as the app reads it: `%74oken` is `token`. A malformed escape is matched as written.
 *
 * @param name The name as it stands in the query string
 * @returns The decoded name
 */
function decodeParameterName(name: string): string {
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

/** A literal or parsed object, as opposed to a class instance such as a `Date` or one of Sentry's scopes. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}
